/**
 * Utilidades de mapa compartidas entre FleetMap y DelivererMapPanel.
 * Centraliza lógica de polyline, íconos Leaflet y configuración de tiles.
 */
import L from 'leaflet';
import Axios from '../config/axios-v1-config';
import { getTile } from '../config/basemaps';

// ── Tile layers ──────────────────────────────────────────────
// La URL del basemap ya NO se hardcodea aquí: deriva de la fuente única
// `src/config/basemaps.js` (configurable desde Configuración de empresa).
// Se exponen como getters para que cada lectura refleje el basemap activo;
// los consumidores reactivos deben preferir el hook useBasemapTile.
// Re-exportamos getTile y getActiveBasemapId por conveniencia.
export { getTile } from '../config/basemaps';

export const TILE_LIGHT = getTile(false);
export const TILE_DARK = getTile(true);

// ── Driver status ─────────────────────────────────────────────
// online = naranja de marca (el repartidor en vivo es el foco y queda inconfundible,
// distinto del verde "entregado"). inactive = ámbar, offline = gris.
export const STATUS_COLORS = { online: '#F97316', inactive: '#f59e0b', offline: '#9ca3af' };

/**
 * Decodifica una cadena Polyline6 (formato OSRM) a un array de [lat, lng]
 * apto para usar directamente en Leaflet <Polyline>.
 */
export function decodePolyline6(encoded) {
  let index = 0, lat = 0, lng = 0;
  const coords = [];
  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    coords.push([lat / 1e6, lng / 1e6]);
  }
  return coords;
}

/**
 * Llama al optimizer (a través del proxy autenticado del BFF, que inyecta la
 * API key server-side) para obtener una polyline road-snapped a partir de
 * coordenadas GPS crudas. Devuelve { polyline, totals }.
 */
export async function fetchPolylineFromCoords(points) {
  const coords = points.map((p) => ({ lat: p.latitude, lon: p.longitude }));
  try {
    const res = await Axios.post('/route-optimizer/polyline-from-coords', { coordinates: coords });
    return res.data;
  } catch (error) {
    throw new Error(`Optimizer error ${error.response?.status ?? error.message}`);
  }
}

/**
 * Muestrea un array uniformemente para no superar maxCount puntos.
 * Preserva siempre el primer y último elemento.
 */
export function samplePoints(points, maxCount) {
  if (points.length <= maxCount) return points;
  const last = points.length - 1;
  return Array.from({ length: maxCount }, (_, i) => points[Math.round(i * last / (maxCount - 1))]);
}

/**
 * Crea el ícono de un repartidor para Leaflet: badge circular compacto con la
 * silueta de un auto, color por estado. El nombre se muestra en el popup.
 * @param {string} _text     – (no usado; se conserva por compatibilidad)
 * @param {string} color     – Color de fondo del marcador
 * @param {boolean} isSelected – Si está seleccionado (más grande)
 */
export function createDriverIcon(_text, color, isSelected = false) {
  const size = isSelected ? 34 : 28;
  const car = Math.round(size * 0.6);
  const border = isSelected ? 3 : 2;
  return L.divIcon({
    className: 'custom-driver-marker',
    html: `<div style="background-color:${color};width:${size}px;height:${size}px;border-radius:50%;border:${border}px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;"><svg width="${car}" height="${car}" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 2],
  });
}

/**
 * Crea el ícono teardrop de un punto de entrega (verde).
 */
export function createDeliveredIcon() {
  return L.divIcon({
    className: 'custom-delivered-marker',
    html: `<div style="background-color:#22c55e;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><div style="transform:rotate(45deg);color:white;font-weight:700;font-size:11px;">✓</div></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -26],
  });
}

/**
 * Crea el ícono teardrop de un punto fallido (rojo).
 */
export function createFailedIcon() {
  return L.divIcon({
    className: 'custom-failed-marker',
    html: `<div style="background-color:#ef4444;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><div style="transform:rotate(45deg);color:white;font-weight:700;font-size:11px;">✕</div></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -26],
  });
}

/**
 * Crea el ícono de punto pendiente (naranja con "P").
 */
export function createPendingIcon() {
  return L.divIcon({
    className: 'custom-pending-marker',
    html: `<div style="background-color:#f59e0b;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><div style="transform:rotate(45deg);color:#1f2937;font-weight:700;font-size:10px;">P</div></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
}

/**
 * Crea el ícono de punto de historial (dot azul).
 * @param {boolean} isHead – Si es el punto más reciente (más grande)
 */
export function createHistoryIcon(isHead) {
  return L.divIcon({
    className: 'custom-history-marker',
    html: `<div style="background-color:${isHead ? '#1d4ed8' : '#60a5fa'};width:${isHead ? 14 : 8}px;height:${isHead ? 14 : 8}px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></div>`,
    iconSize: isHead ? [14, 14] : [8, 8],
    iconAnchor: isHead ? [7, 7] : [4, 4],
  });
}

/**
 * Crea un marcador circular con numero de orden para entregas y fallos.
 * @param {number} number      – Número de orden (1, 2, 3…)
 * @param {boolean} isDelivered – Verde si entregado, rojo si fallido
 */
export function createNumberedIcon(number, isDelivered) {
  const bg = isDelivered ? '#22c55e' : '#ef4444';
  return L.divIcon({
    className: 'custom-numbered-marker',
    html: `<div style="background-color:${bg};width:28px;height:28px;border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><span style="color:white;font-weight:700;font-size:11px;line-height:1;">${number}</span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

/**
 * Crea el marcador de inicio del recorrido (punto 0, color indigo).
 */
export function createStartIcon() {
  return L.divIcon({
    className: 'custom-start-marker',
    html: `<div style="background-color:#6366f1;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;"><span style="color:white;font-weight:700;font-size:13px;line-height:1;">★</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Builders paramétricos genéricos (audit #8)
// Unifican los íconos circulares/teardrop de TrackingMap, FleetMap, gpsTracking
// y LocationsTab. Las funciones específicas anteriores (createDeliveredIcon,
// createPendingIcon, …) se mantienen por compatibilidad — pueden migrarse a
// estos helpers en un pase posterior.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * createCircleMarker — marcador circular con borde, sombra y glyph opcional
 * (Material Symbol, texto/initials o SVG crudo). Soporta selección (más grande)
 * y alarma (anillo pulsante CSS `.lt-pulse-ring`).
 *
 * @param {object} opts
 * @param {number} [opts.size=32]        Diámetro en px.
 * @param {string} opts.color            Color de fondo (hex).
 * @param {string} [opts.borderColor='#fff']
 * @param {number} [opts.borderWidth=2]
 * @param {string} [opts.glyph]          Glyph (nombre material / texto / svg).
 * @param {string} [opts.glyphFont='text']  'material' | 'text' | 'svg'.
 * @param {string} [opts.glyphColor='#fff']
 * @param {boolean} [opts.selected=false]   Anillo naranja de selección extra.
 * @param {boolean} [opts.alarm=false]      Anillo rojo SOLID (halo estático).
 * @param {boolean} [opts.pulse=false]      Añade anillo pulsante .lt-pulse-ring.
 * @param {string}  [opts.shadow]            Override completo del box-shadow.
 * @param {string}  [opts.className='']      Extra className en el divIcon.
 */
export function createCircleMarker({
  size = 32,
  color,
  borderColor = '#fff',
  borderWidth = 2,
  glyph,
  glyphFont = 'text',
  glyphColor = '#fff',
  selected = false,
  alarm = false,
  pulse = false,
  shadow,
  className = '',
}) {
  const ringShadow = alarm
    ? '0 0 0 3px rgba(220,38,38,.9),0 2px 6px rgba(0,0,0,.4)'
    : selected
      ? '0 0 0 4px rgba(249,115,22,.45),0 2px 8px rgba(0,0,0,.4)'
      : '0 2px 6px rgba(0,0,0,0.35)';
  const boxShadow = shadow || ringShadow;

  let inner = '';
  if (glyph) {
    if (glyphFont === 'material') {
      const fontSize = Math.round(size * 0.62);
      inner = `<span class="material-symbols-outlined" style="font-size:${fontSize}px;line-height:1;color:${glyphColor} !important;">${glyph}</span>`;
    } else if (glyphFont === 'svg') {
      inner = glyph;
    } else {
      const fontSize = Math.round(size * 0.35);
      inner = `<span style="color:${glyphColor};font-weight:700;font-size:${fontSize}px;line-height:1;text-align:center;">${glyph}</span>`;
    }
  }

  const pulseRing = pulse
    ? `<span class="lt-pulse-ring" style="background:${color};"></span>`
    : '';

  const html = `<div style="position:relative;width:${size}px;height:${size}px;">${pulseRing}<div style="position:relative;width:100%;height:100%;background-color:${color};border-radius:50%;border:${borderWidth}px solid ${borderColor};box-shadow:${boxShadow};display:flex;align-items:center;justify-content:center;">${inner}</div></div>`;

  return L.divIcon({
    className,
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 2],
  });
}

/**
 * createTeardropMarker — pin teardrop (cuadrado redondeado rotado -45°).
 * El glyph interno se contra-rota para quedar upright.
 *
 * @param {object} opts
 * @param {number} [opts.size=32]
 * @param {string} opts.color
 * @param {string} [opts.borderColor='#fff']
 * @param {number} [opts.borderWidth=2]
 * @param {string} [opts.content]            Texto/initials/símbolo interno.
 * @param {string} [opts.contentColor='#fff']
 * @param {number} [opts.contentSize]        Override del font-size en px.
 * @param {boolean} [opts.selected=false]    Más grande + sombra más fuerte.
 * @param {string}  [opts.shadow]            Override del box-shadow.
 * @param {string}  [opts.className='']
 */
export function createTeardropMarker({
  size = 32,
  color,
  borderColor = '#fff',
  borderWidth = 2,
  content = '',
  contentColor = '#fff',
  contentSize,
  selected = false,
  shadow,
  className = '',
}) {
  const finalSize = selected ? Math.round(size * 1.18) : size;
  const finalBorder = selected ? Math.max(borderWidth + 1, 3) : borderWidth;
  const boxShadow = shadow || (selected
    ? '0 2px 6px rgba(0,0,0,0.35)'
    : '0 2px 4px rgba(0,0,0,0.3)');
  const fontSize = contentSize != null
    ? contentSize
    : Math.max(10, Math.round(finalSize * 0.32));
  const inner = content
    ? `<div style="transform:rotate(45deg);color:${contentColor};font-weight:700;font-size:${fontSize}px;line-height:1;text-align:center;">${content}</div>`
    : '';
  const html = `<div style="background-color:${color};width:${finalSize}px;height:${finalSize}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:${finalBorder}px solid ${borderColor};box-shadow:${boxShadow};display:flex;align-items:center;justify-content:center;">${inner}</div>`;
  return L.divIcon({
    className,
    html,
    iconSize: [finalSize, finalSize],
    iconAnchor: [finalSize / 2, finalSize],
    popupAnchor: [0, -finalSize],
  });
}

/**
 * Retorna las coordenadas [lat, lng] de un paquete.
 * Prioriza coords reales (capturadas en la entrega) sobre las de la dirección.
 * @returns {[number, number] | null}
 */
export function getPackageCoords(pkg) {
  if (pkg.coords_latitude != null && pkg.coords_longitude != null) {
    return [pkg.coords_latitude, pkg.coords_longitude];
  }
  if (pkg.shipping_latitude != null && pkg.shipping_longitude != null) {
    return [pkg.shipping_latitude, pkg.shipping_longitude];
  }
  return null;
}
